import http from '@/lib/http';
import {
  cmsResourceTagsPath,
  cmsReviewPath,
  cmsReviewsGoogleBusinessConnectionPath,
  cmsReviewsGooglePlacePath,
  cmsReviewsGooglePlacesPath,
  cmsReviewsNotificationsPath,
  cmsReviewsOverviewPath,
  cmsReviewsPath,
  publishCmsReviewPath,
  reorderCmsReviewsPath,
  restoreCmsReviewPath,
  unpublishCmsReviewPath,
} from '@/lib/routes';
import type { PaginationInfo } from '@/types/pagination';
import type { MyReviewsFilter } from './constants';
import type {
  AddReviewFormData,
  AttorneyOption,
  GoogleBusiness,
  GoogleBusinessPlace,
  GoogleBusinessSearchResult,
  MyReviewRow,
  ReviewRow,
} from './types';

type ReviewPayload = {
  review: AddReviewFormData & {
    rating?: number | null;
    showStarRatingOnWebsite?: boolean;
  };
};

export type PublishReviewResponse = {
  review?: ReviewRow;
  myReview?: MyReviewRow;
};

export type DeleteReviewResponse = {
  totalReviewsCount?: number;
};

export type RevertReviewResponse = {
  review?: ReviewRow | MyReviewRow;
};

export type FetchMyReviewsResponse = {
  myReviews?: MyReviewRow[];
  pagination?: PaginationInfo;
};

export type FetchReceivedReviewsResponse = {
  receivedReviews?: ReviewRow[];
  pagination?: PaginationInfo;
  newReceivedReviewsCount?: number;
};

export type ConnectGoogleBusinessResponse = {
  googleBusiness?: GoogleBusiness;
};

export async function searchGooglePlaces(query: string) {
  const response = await http.get(cmsReviewsGooglePlacesPath(), { params: { q: query } });
  return (response.data?.results || []) as GoogleBusinessSearchResult[];
}

export async function fetchGooglePlace(placeId: string) {
  const response = await http.get(cmsReviewsGooglePlacePath(placeId));
  return response.data?.place as GoogleBusinessPlace;
}

export async function connectGoogleBusiness(placeId: string) {
  const response = await http.post(cmsReviewsGoogleBusinessConnectionPath(), { placeId });
  return response.data as ConnectGoogleBusinessResponse;
}

export async function updateReview(reviewId: string, payload: ReviewPayload) {
  const response = await http.patch(cmsReviewPath(reviewId), payload);
  return response.data as { review?: ReviewRow; myReview?: MyReviewRow };
}

export async function publishReview(reviewId: string) {
  const response = await http.patch(publishCmsReviewPath(reviewId));
  return response.data as PublishReviewResponse;
}

export async function unpublishReview(reviewId: string) {
  const response = await http.patch(unpublishCmsReviewPath(reviewId));
  return response.data as PublishReviewResponse;
}

export async function deleteReview(reviewId: string) {
  const response = await http.delete(cmsReviewPath(reviewId));
  return response.data as DeleteReviewResponse;
}

export async function revertReview(reviewId: string) {
  const response = await http.patch(restoreCmsReviewPath(reviewId));
  return response.data as RevertReviewResponse;
}

export async function fetchMyReviews(params: {
  page?: number;
  search?: string;
  filter?: MyReviewsFilter;
}) {
  const response = await http.get(cmsReviewsPath(), {
    params: {
      page: params.page,
      search: params.search,
      filter: params.filter === 'all' ? undefined : params.filter,
    },
  });
  return {
    myReviews: response.data?.myReviews as MyReviewRow[] | undefined,
    pagination: response.data?.myReviewsPagination
      ? {
          currentPage: response.data.myReviewsPagination.currentPage,
          totalPages: response.data.myReviewsPagination.totalPages,
          totalCount: response.data.myReviewsPagination.totalCount,
          perPage: response.data.myReviewsPagination.perPage,
        }
      : undefined,
  } satisfies FetchMyReviewsResponse;
}

export async function fetchReceivedReviews(params: { page?: number }) {
  const response = await http.get(cmsReviewsOverviewPath(), {
    params: { page: params.page },
  });
  return {
    receivedReviews: response.data?.receivedReviews as ReviewRow[] | undefined,
    pagination: response.data?.receivedReviewsPagination
      ? {
          currentPage: response.data.receivedReviewsPagination.currentPage,
          totalPages: response.data.receivedReviewsPagination.totalPages,
          totalCount: response.data.receivedReviewsPagination.totalCount,
          perPage: response.data.receivedReviewsPagination.perPage,
        }
      : undefined,
    newReceivedReviewsCount: response.data?.newReceivedReviewsCount,
  } satisfies FetchReceivedReviewsResponse;
}

export async function reorderReviews(reviewIds: string[]) {
  const response = await http.patch(reorderCmsReviewsPath(), { reviewIds });
  return response.data as { success?: boolean };
}

export async function destroyGoogleBusiness() {
  const response = await http.delete(cmsReviewsGoogleBusinessConnectionPath());
  return response.data as { success?: boolean };
}

export async function dismissReceivedReviewsNotification() {
  const response = await http.delete(cmsReviewsNotificationsPath());
  return response.data as { success?: boolean };
}

export async function fetchReviewAttorneys(): Promise<AttorneyOption[]> {
  const response = await http.get<{ tags: AttorneyOption[] }>(cmsResourceTagsPath(), {
    params: { tagType: 'profile' },
  });
  return response.data.tags;
}
